/**
 * What BIM mode does to the app's chrome, from production's Mighty BIM widget (its `nz-bim` stylesheet and BIM_HIDE).
 * BIM is a view of the same place, not a separate application, so most of the chrome stays; what goes is what describes
 * a map that is not on screen:
 *
 *   - the theme cards (in BIM there is no theme to pick; the bar and the Map · BIM · Globe switch keep their place);
 *   - the scale bar (it measures a map that is not drawn);
 *   - the map-only tools in the controller: Sketch and Analysis Pro work on the map's layers and its geometry, none
 *     of which is on screen in BIM, so they are hidden rather than left to fail quietly (Data Passport too, unless the
 *     project's model takes added data: BIM_DATA_TOOLS). Mighty Dig
 *     draws its permits on the map too, so it goes with them. Snapshot and Print Pro stay, as in production's BIM bar
 *     (its `hideInBim`): they work on the model through the BIM tool engine (BIM_ENGINE_TOOLS). Layers Pro (which
 *     becomes the model's own layer list) and Information stay too, and the controller shrinks to the four.
 *
 * The left map-tools column (zoom, home, previous / next extent, find my location, the compass) stays, and drives the
 * model camera (lib/viewCamera, features/bim/seams.ts): production hid it with the map's view, which left BIM with no
 * buttons for any of it.
 *
 * Map and Globe are unchanged. The rules are in bimChrome.css, keyed on `.mm-app[data-view='bim']`; a map-only tool
 * whose pane was open stays open underneath, hidden, and is back as it was on leaving BIM.
 */

/** The controller tools hidden in BIM (production's `hideInBim`, by tool id). */
export const MAP_ONLY_TOOLS: ReadonlySet<string> = new Set(['sketch', 'analysis', 'dig']);

/**
 * The controller tools that work on the model only where the project's model takes added data: Data Passport, which
 * in BIM adds to the model (the design contract data-passport-in-bim-design-2026-10-29.md). The switch is the BIM
 * settings' `gridWkid` (lib/bim/settings.ts): a project that names no grid keeps it hidden in BIM, as before.
 */
export const BIM_DATA_TOOLS: ReadonlySet<string> = new Set(['data']);

/**
 * The controller tools that work on the model in BIM: they get the BIM tool engine (features/bim/bimToolEngine), so a
 * capture is the model's canvas, the camera is the model's and a drawn overlay lies on the model. The others keep the
 * map's engine beneath.
 */
export const BIM_ENGINE_TOOLS: ReadonlySet<string> = new Set(['snapshot', 'print']);

/** Whether a controller tool is on screen in a view; `bimData`: the model takes added data (the project names a grid). */
export function toolShownIn(toolId: string, inBim: boolean, bimData = false): boolean {
  if (!inBim) return true;
  if (BIM_DATA_TOOLS.has(toolId)) return bimData;
  return !MAP_ONLY_TOOLS.has(toolId);
}
